import { deepEqual, findIntersectionsBetweenSets } from '@anecdotes/utils';
import { Injectable } from '@angular/core';
import { select, Store } from '@ngrx/store';
import { combineLatest, Observable, of } from 'rxjs';
import { distinctUntilChanged, filter, map, switchMap } from 'rxjs/operators';
import {
  DataManipulationConfig,
  FieldState,
  FilterSearchOverlap,
  OptionState,
  OptionStateWithCount,
  SortDirectionType
} from '../../models';
import { DataFilterAction } from '../../store/actions';
import { DataManipulationSelectors } from '../../store/selectors';
import { FilterDefinitionStoreService } from '../filter-definition-store/filter-definition-store.service';

@Injectable()
export class DataFilterManagerService {
  constructor(private store: Store, private filterDefinitionStoreService: FilterDefinitionStoreService) {}

  getAllData<TData>(filterInstanceId: string): Observable<TData[]> {
    return this.delayObservableUntilInitialization(filterInstanceId)
      .pipe(
        switchMap(() => this.store.select(DataManipulationSelectors.Filter.CreateAllDataSelector(filterInstanceId))),
        filter(Boolean),
      );
  }

  getDataFilterEvent<TData>(filterInstanceId: string): Observable<TData[]> {
    return this.delayObservableUntilInitialization(filterInstanceId)
      .pipe(
        switchMap(() => this.store.select(DataManipulationSelectors.Aggregation.createFilteredDataSelector(filterInstanceId))),
        filter(Boolean),
        distinctUntilChanged((firstData, secondData) => deepEqual(firstData, secondData))
      );
  }

  getSortedData<TData>(filterInstanceId: string): Observable<TData[]> {
    return this.delayObservableUntilInitialization(filterInstanceId).pipe(
      switchMap(() =>
        combineLatest([
          this.store.select(DataManipulationSelectors.Sort.createOrderedDataIdsSelector(filterInstanceId)),
          this.store.select(DataManipulationSelectors.Filter.createDataToFilterSelector(filterInstanceId)),
        ]),
      ),
      map(([orderedDataIds, allData]) => {
        return orderedDataIds.map((dataId) => allData[dataId]).filter(Boolean);
      }),
    );
  }

  getFilteringFields(filterInstanceId: string): Observable<FieldState[]> {
    return this.store.select(
      DataManipulationSelectors.Filter.createFilteringFieldsSelector(filterInstanceId)
    );
  }

  getFilteringOptions(filterInstanceId: string): Observable<{
    [fieldId: string]: { [optionId: string]: OptionState };
  }> {
    return this.store.pipe(
      select(DataManipulationSelectors.Filter.createCheckedOptionsSelector(filterInstanceId)),
      filter((checkedOptions) => !!checkedOptions)
    );
  }

  getAppliedFilterOptions(filterInstanceId: string, onlyDisplayed = true): Observable<OptionState[]> {
    return this.getFilteringOptions(filterInstanceId).pipe(
      map((filters) => {
        const options = Object.values(filters).flatMap((options) => Object.values(options));
        const appliedFiltersOptions = this.getAppliedFiltersOptions(options, onlyDisplayed).filter(
          (option: OptionState) => onlyDisplayed ? option.isDisplayed : true
        );

        return appliedFiltersOptions;
      })
    );
  }

  getFieldRelatedActiveOptions(filterInstanceId: string, fieldId: string): Observable<OptionState[]> {
    return this.store.pipe(
      select(DataManipulationSelectors.Filter.createFieldRelatedCheckedOptionsSelector(filterInstanceId, fieldId))
    );
  }

  getFieldRelatedOptions(filterInstanceId: string, fieldId: string): Observable<OptionState[]> {
    return this.store.pipe(
      select(DataManipulationSelectors.Filter.createFieldRelatedOptionsSelector(filterInstanceId, fieldId))
    );
  }

  getFieldRelatedNestedDataIds(filterInstanceId: string, fieldId: string): Observable<Set<string>> {
    return this.store.pipe(
      select(DataManipulationSelectors.Filter.createFieldRelatedNestedDataIdsSelector(filterInstanceId, fieldId))
    );
  }

  getGroupRelatedNestedDataIds(filterInstanceId: string, nestedGroupId: string): Observable<Set<string>> {
    return this.store
      .pipe(select(DataManipulationSelectors.Filter.createNestedGroupIdFieldsSelector(filterInstanceId, nestedGroupId)))
      .pipe(
        switchMap((nestedGroupRelatedFields) => {
          const streams = nestedGroupRelatedFields.map((field) => {
            return this.store.pipe(
              select(
                DataManipulationSelectors.Filter.createFieldRelatedNestedDataIdsSelector(filterInstanceId, field.id)
              )
            );
          });

          if (!streams.length) {
            return of([]);
          }

          return combineLatest(streams);
        }),
        map((nestedIdSets) => {
          const notNullSets = nestedIdSets.filter((set) => set);

          return notNullSets.length ? findIntersectionsBetweenSets(notNullSets) : null;
        })
      );
  }

  getOptionState(filterInstanceId: string, fieldId: string, optionId: string): Observable<OptionState> {
    return this.store.pipe(
      select(DataManipulationSelectors.Filter.createOptionSelector(filterInstanceId, fieldId, optionId))
    );
  }

  getOptionsWithCountForFieldSelector(filterInstanceId: string, fieldId: string): Observable<OptionStateWithCount[]> {
    return this.store.pipe(
      select(DataManipulationSelectors.Filter.createOptionsWithCountForFieldSelector(filterInstanceId, fieldId))
    );
  }

  getOptionCount(filterInstanceId: string, fieldId: string, optionId: string): Observable<number> {
    return this.store.pipe(
      select(DataManipulationSelectors.Filter.createOptionCountCalculationSelector(filterInstanceId, fieldId, optionId))
    );
  }

  getSearchOverlaps(filterInstanceId: string): Observable<FilterSearchOverlap[]> {
    return this.store.pipe(select(DataManipulationSelectors.Search.createSearchOverlapsSelector(filterInstanceId)));
  }

  getSearchText(filterInstanceId: string): Observable<string> {
    return this.store.pipe(select(DataManipulationSelectors.Search.createSearchTextSelector(filterInstanceId)));
  }

  setSearchText(filterInstanceId: string, searchValue: string): void {
    this.store.dispatch(DataFilterAction.search({ instanceId: filterInstanceId, searchValue }));
  }

  getSortOptionId(filterInstanceId: string): Observable<string> {
    return this.store.pipe(select(DataManipulationSelectors.Sort.createSortOptionSelector(filterInstanceId)));
  }

  getSortDirection(filterInstanceId: string): Observable<SortDirectionType> {
    return this.store.pipe(select(DataManipulationSelectors.Sort.createSortDirectionSelector(filterInstanceId)));
  }

  isEmptyBySearch(filterInstanceId: string): Observable<boolean> {
    return this.store.pipe(select(DataManipulationSelectors.Search.createIsIsEmptyBySearchSelector(filterInstanceId)));
  }

  isEmptyByFilter(filterInstanceId: string): Observable<boolean> {
    return this.store.pipe(DataManipulationSelectors.Filter.createIsIsEmptyByFilterSelector(filterInstanceId));
  }

  toggleOptions(filterInstanceId: string, optionsData: { [fieldId: string]: string[] }): void {
    this.store.dispatch(DataFilterAction.toggleOptions({ instanceId: filterInstanceId, optionsData }));
  }

  selectOptions(filterInstanceId: string, optionsData: { [fieldId: string]: string[] }): void {
    this.store.dispatch(DataFilterAction.selectOptions({ instanceId: filterInstanceId, optionsData }));
  }

  unSelectOptions(filterInstanceId: string, optionsData: { [fieldId: string]: string[] }): void {
    this.store.dispatch(DataFilterAction.unSelectOptions({ instanceId: filterInstanceId, optionsData }));
  }

  setOptions(filterInstanceId: string, optionsData: { [fieldId: string]: string[] }): void {
    this.store.dispatch(DataFilterAction.setOptions({ instanceId: filterInstanceId, optionsData }));
  }

  setData<TData>(filterInstanceId: string, data: TData[], shouldSetPredefinedSearchValue = true): void {
    this.store.dispatch(
      DataFilterAction.setDataForFiltering({
        instanceId: filterInstanceId,
        data: data,
        shouldSetPredefinedSearchValue,
      }),
    );
  }

  init<TData>(filterDefinition: DataManipulationConfig<TData>): void {
    this.filterDefinitionStoreService.add(filterDefinition.filterInstanceId, filterDefinition);
  }

  reset(filterInstanceId: string): void {
    this.store.dispatch(DataFilterAction.reset({ instanceId: filterInstanceId }));
  }

  resetSearch(filterInstanceId: string): void {
    this.store.dispatch(DataFilterAction.resetSearch({ instanceId: filterInstanceId }));
  }

  resetField(filterInstanceId: string, fieldId: string): void {
    this.store.dispatch(DataFilterAction.resetFields({ instanceId: filterInstanceId, fieldIds: [fieldId] }));
  }

  destroy(filterInstanceId: string): void {
    this.store.dispatch(DataFilterAction.destroy({ instanceId: filterInstanceId }));
  }

  toggleSortDirection(filterInstanceId: string): void {
    this.store.dispatch(DataFilterAction.toggleSortDirection({ instanceId: filterInstanceId }));
  }

  changeSortOption(filterInstanceId: string, sortOptionId: string, sortDirection?: SortDirectionType): void {
    this.store.dispatch(DataFilterAction.changeSortOption({ instanceId: filterInstanceId, sortOptionId, sortDirection }));
  }

  private getAppliedFiltersOptions(options: OptionState[], onlyDisplayed = true): OptionState[] {
    return options.filter((option: OptionState) => !!option.isChecked && (onlyDisplayed ? this.handleDisplayedProp(option.isDisplayed) : true));
  }

  private handleDisplayedProp(isDisplayed: boolean | undefined): boolean {
    return isDisplayed || isDisplayed === undefined;
  }

  private delayObservableUntilInitialization(filterInstanceId: string): Observable<true> {
    return this.store
      .select(DataManipulationSelectors.Filter.CreateIsFilterInstanceInitializedSelector(filterInstanceId))
      .pipe(
        filter(Boolean)
      );
  }
}
